import React, { useEffect, useMemo, useState } from 'react';
import SupervisorLayout from '../../components/SupervisorLayout';
import DateField from '../../components/DateField';
import LotteryCodeField from '../../components/LotteryCodeField';
import { fetchSupervisorWinningNumberSummary } from '../../api/supervisor';
import { todayISO } from '../../utils/date';
import { fmtWin4 } from '../../utils/lottery';
import './SupervisorWinningNumbersPage.css';

const money = (n) => (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Filtre Loterie optionnel ajoute le 2026-09-17 (demande explicite) — vide =
// toutes les loteries (comportement d'avant). Purement client-side : la
// reponse contient deja toutes les loteries de la date, pas besoin d'un
// nouveau parametre backend — on filtre juste les lignes affichees (et le
// total, qui doit refleter le filtre, pas le grand total global).
const SupervisorWinningNumbersPage = () => {
  const [date, setDate] = useState(todayISO());
  const [lottery, setLottery] = useState('');
  const [lotteryName, setLotteryName] = useState('');
  const [data, setData] = useState({ rows: [], grand_total: 0, grand_tickets: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    fetchSupervisorWinningNumberSummary({ date })
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setError('Impossible de charger les numeros gagnants.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [date]);

  const rows = useMemo(
    () => (lottery ? data.rows.filter((r) => String(r.tirage_code) === String(lottery)) : data.rows),
    [data.rows, lottery],
  );
  const totalTickets = rows.reduce((s, r) => s + r.winning_tickets, 0);
  const totalGain = rows.reduce((s, r) => s + r.total_gain, 0);

  return (
    <SupervisorLayout>
      <h2 className="svwn-title">Numeros gagnants</h2>

      <div className="svwn-row">
        <label className="svwn-label">Date</label>
        <DateField className="svwn-input" value={date} onChange={setDate} />
      </div>

      <div className="svwn-row">
        <label className="svwn-label">Loterie</label>
        <LotteryCodeField
          value={lottery}
          name={lotteryName}
          onChange={(code) => { setLottery(code); setLotteryName(''); }}
          onResolve={(code, nm) => { setLottery(code); setLotteryName(nm); }}
          inputClass="svwn-input svwn-input-sm"
          iconClass="svwn-icon-btn"
          nameClass="svwn-scope-name"
          placeholder="Toutes"
        />
        {lottery && (
          <button type="button" className="svwn-clear-btn" onClick={() => { setLottery(''); setLotteryName(''); }}>
            ✕
          </button>
        )}
      </div>

      {error && <p className="svwn-error">{error}</p>}
      {loading ? (
        <p>Chargement...</p>
      ) : (
        <div className="svwn-table-wrap">
          <table className="svwn-table">
            <thead>
              <tr>
                <th>Tirage</th>
                <th>Numero</th>
                <th>Win4</th>
                <th>Statut</th>
                <th>Fiches gagnées (mes banks)</th>
                <th>Somme totale (mes banks)</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={6} className="svwn-empty">
                  {lottery ? 'Aucun lot gagnant pour cette loterie ce jour.' : 'Aucun lot gagnant saisi ce jour.'}
                </td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.tirage_code}>
                  <td>{r.tirage_code} — {r.tirage_name}</td>
                  <td className="svwn-num">{r.numbers}</td>
                  <td className="svwn-num">{fmtWin4(r.win4)}</td>
                  <td>{r.status_label}</td>
                  <td className="svwn-center">{r.winning_tickets}</td>
                  <td className="svwn-money">{money(r.total_gain)}</td>
                </tr>
              ))}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr className="svwn-total-row">
                  <td colSpan={4}>Total</td>
                  <td className="svwn-center">{totalTickets}</td>
                  <td className="svwn-money">{money(totalGain)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </SupervisorLayout>
  );
};

export default SupervisorWinningNumbersPage;
